import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Button, Stack, Chip, Avatar, Divider,
  Accordion, AccordionSummary, AccordionDetails, Card, Snackbar, Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get } from 'lodash';
import Page from '../../components/Page';
import config from '../../util/config';
import { getSingleCoursesAdmin, updateCoursePageContent } from 'src/axiosLib/services/course/admin';
import { getSingleCourse } from 'src/axiosLib/services/course/user';
import { getTeachersByCourseAdmin } from 'src/axiosLib/services/teacher/admin';
import { getTeachersByCourse } from 'src/axiosLib/services/teacher/user';
import { getDashboardBase } from 'src/util/helpers';
import FeaturesEditor from 'src/components/course/FeaturesEditor';
import SyllabusEditor from 'src/components/course/SyllabusEditor';

import { adminLevel } from 'src/axiosLib/services/institution/admin';

// Hardcoded data removed — features & syllabus now come from the database

const TEACHERS = [
  { name: 'ডাঃ আসিফ', role: 'Biology, Physics & Chemistry', image: '/public/img/asif/review/02.jpg' },
  { name: 'K@bir Sizer', role: 'English & GK', image: '/public/img/asif/review/03.jpg' },
];

export default function CoolCoursePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  const [course, setCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const isInDashboard = window.location.pathname.includes('dashboard');
  const [featuresEditorOpen, setFeaturesEditorOpen] = useState(false);
  const [syllabusEditorOpen, setSyllabusEditorOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [roleChecked, setRoleChecked] = useState(false);

  // Gate: redirect if institution lacks NEW_CLASSIC_EXAM_ADMIN_DASHBOARD role
  useEffect(() => {
    if (!isInDashboard) { setRoleChecked(true); return; }
    adminLevel((error, data) => {
      if (!error) {
        const roles = get(data, 'admin.institutionId.agencyWideRoles', []);
        if (!roles.includes('NEW_CLASSIC_EXAM_ADMIN_DASHBOARD')) {
          navigate(getDashboardBase() + '/app', { replace: true });
          return;
        }
      }
      setRoleChecked(true);
    });
  }, []);

  const saveFeatures = (newFeatures) => {
    updateCoursePageContent({ courseId, features: newFeatures }, (err, data) => {
      if (!err) {
        setCourse(prev => ({ ...prev, features: newFeatures }));
        setSnackbar({ open: true, message: 'Features saved!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Error saving features', severity: 'error' });
      }
    });
    setFeaturesEditorOpen(false);
  };

  const saveSyllabus = (newEntries) => {
    updateCoursePageContent({ courseId, syllabusEntries: newEntries }, (err, data) => {
      if (!err) {
        // Refetch course to get populated data
        getSingleCoursesAdmin(courseId, (e2, d2) => {
          if (!e2) setCourse(get(d2, 'course', {}));
        });
        setSnackbar({ open: true, message: 'Syllabus saved!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Error saving syllabus', severity: 'error' });
      }
    });
    setSyllabusEditorOpen(false);
  };

  useEffect(() => {
    if (courseId) {
      // Try admin API first (if logged in), fallback to public API
      getSingleCoursesAdmin(courseId, (error, data) => {
        if (!error) {
          setCourse(get(data, 'course', {}));
        } else {
          getSingleCourse(courseId, (err2, data2) => {
            if (!err2) setCourse(get(data2, 'course', {}));
          });
        }
      });

      // Fetch teachers for this course
      getTeachersByCourseAdmin(courseId, (error, data) => {
        if (!error) {
          setTeachers(get(data, 'teachers', []));
        } else {
          getTeachersByCourse(courseId, (err2, data2) => {
            if (!err2) setTeachers(get(data2, 'teachers', []));
          });
        }
      });
    }
  }, [courseId]);

  if (!roleChecked || !course) {
    return (
      <Page title="Loading...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Typography sx={{ color: '#666' }}>Loading course details...</Typography>
        </Box>
      </Page>
    );
  }

  const tabs = ['ওভারভিউ', 'সিলেবাস', 'শিক্ষক মণ্ডলী'];
  const finalPrice = get(course, 'discountedPrice') || get(course, 'price', 0);
  const originalPrice = get(course, 'price', 0);
  const hasDiscount = get(course, 'discountedPrice') && get(course, 'discountedPrice') < originalPrice;

  return (
    <Page title={get(course, 'title', 'Course Details')}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .cool-course * { font-family: 'Inter', sans-serif; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.6s ease-out both; }
        .fade-up-2 { animation: fadeUp 0.6s ease-out 0.15s both; }
        .fade-up-3 { animation: fadeUp 0.6s ease-out 0.3s both; }
      `}</style>

      <Box className="cool-course">
        {/* ═══════ HERO BANNER ═══════ */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
          py: { xs: 5, md: 7 }, position: 'relative', overflow: 'hidden',
        }}>
          {/* Decorative blobs */}
          <Box sx={{
            position: 'absolute', top: -80, right: -80, width: 300, height: 300,
            borderRadius: '50%', background: 'rgba(99,102,241,0.15)', filter: 'blur(60px)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -60, left: -60, width: 200, height: 200,
            borderRadius: '50%', background: 'rgba(0,212,255,0.1)', filter: 'blur(50px)',
          }} />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
              {/* Course image */}
              <Box className="fade-up" sx={{
                width: { xs: '100%', md: 200 }, height: { xs: 180, md: 140 },
                borderRadius: 3, overflow: 'hidden', flexShrink: 0,
                boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
              }}>
                <Box component="img" src={config.BASE_API_URL + get(course, 'image')}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>

              <Box className="fade-up-2" sx={{ flex: 1 }}>
                <Typography sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 900, color: '#fff',
                  lineHeight: 1.3, mb: 1,
                }}>
                  {get(course, 'title', '')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', mb: 2, maxWidth: 600 }}>
                  {get(course, 'description', '')}
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Chip icon={<Icon icon="mdi:clock-outline" />}
                    label={get(course, 'time', get(course, 'duration', '') + ' দিন')}
                    sx={{ background: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#a5b4fc' } }} />
                  <Chip icon={<Icon icon="mdi:account-group" />}
                    label={get(course, 'type', 'Medical Admission')}
                    sx={{ background: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#a5b4fc' } }} />
                  <Chip icon={<Icon icon="mdi:check-decagram" />}
                    label={get(course, 'isActive') ? 'সক্রিয়' : 'নিষ্ক্রিয়'}
                    sx={{
                      background: get(course, 'isActive') ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
                      color: '#fff', fontWeight: 600,
                      '& .MuiChip-icon': { color: get(course, 'isActive') ? '#4ade80' : '#f87171' },
                    }} />
                </Stack>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* ═══════ CONTENT ═══════ */}
        <Box sx={{ py: 5, background: '#f5f7fb', minHeight: '60vh' }}>
          <Container maxWidth="lg">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">
              {/* LEFT - Main Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Tabs */}
                <Box sx={{
                  display: 'flex', gap: 0, background: '#fff', borderRadius: 3,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 4, overflow: 'hidden',
                }}>
                  {tabs.map((tab, i) => (
                    <Button key={i} onClick={() => setActiveTab(i)}
                      sx={{
                        flex: 1, py: 2, borderRadius: 0, fontWeight: 700, fontSize: '0.9rem',
                        color: activeTab === i ? '#4338ca' : '#888',
                        borderBottom: activeTab === i ? '3px solid #4338ca' : '3px solid transparent',
                        background: activeTab === i ? 'rgba(67,56,202,0.04)' : 'transparent',
                        textTransform: 'none',
                        '&:hover': { background: 'rgba(67,56,202,0.06)' },
                      }}>
                      {tab}
                    </Button>
                  ))}
                </Box>

                {/* Tab Content */}
                {activeTab === 0 && (
                  <Box className="fade-up">
                    {/* Features Grid */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1e1b4b' }}>
                        কোর্সের বিশেষত্ব
                      </Typography>
                      {isInDashboard && (
                        <Button size="small" startIcon={<Icon icon="mdi:pencil" />}
                          onClick={() => setFeaturesEditorOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#4338ca' }}>
                          Edit
                        </Button>
                      )}
                    </Stack>
                    <Box sx={{
                      display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4,
                    }}>
                      {(get(course, 'features', []) || []).map((f, i) => (
                        <Box key={i} sx={{
                          p: 3, borderRadius: 3, background: '#fff',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                          border: '1px solid rgba(67,56,202,0.06)',
                          transition: 'all 0.3s',
                          '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(67,56,202,0.1)' },
                        }}>
                          <Box sx={{
                            width: 44, height: 44, borderRadius: 2, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', mb: 1.5,
                            background: 'linear-gradient(135deg, rgba(67,56,202,0.08), rgba(99,102,241,0.12))',
                          }}>
                            <Icon icon={f.icon || 'mdi:star'} style={{ fontSize: 24, color: '#4338ca' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '0.95rem', mb: 0.5 }}>
                            {f.title}
                          </Typography>
                          <Typography sx={{ color: '#666', fontSize: '0.82rem' }}>{f.description}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Description */}
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', mb: 3 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e1b4b', mb: 2 }}>
                        কোর্স বিবরণ
                      </Typography>
                      <Typography sx={{ color: '#444', lineHeight: 1.8, fontSize: '0.92rem' }}>
                        {get(course, 'description', '')}
                      </Typography>
                    </Card>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box className="fade-up">
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1e1b4b' }}>
                        কোর্সের সিলেবাস
                      </Typography>
                      {isInDashboard && (
                        <Button size="small" startIcon={<Icon icon="mdi:pencil" />}
                          onClick={() => setSyllabusEditorOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#4338ca' }}>
                          Edit
                        </Button>
                      )}
                    </Stack>
                    {(get(course, 'syllabusEntries', []) || []).map((entry, si) => {
                      const subject = entry.subjectId || {};
                      const color = subject.color || '#4338ca';
                      const icon = subject.icon || 'mdi:book-open-variant';
                      const chapters = entry.chapterIds || [];
                      return (
                      <Accordion key={subject._id || si} defaultExpanded={si === 0}
                        sx={{
                          mb: 1.5, borderRadius: '12px !important', overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          '&:before': { display: 'none' },
                          border: `1px solid ${color}15`,
                        }}>
                        <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" style={{ fontSize: 24, color }} />}
                          sx={{ px: 3, py: 1 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{
                              width: 40, height: 40, borderRadius: 2, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              background: `${color}12`,
                            }}>
                              <Icon icon={icon} style={{ fontSize: 22, color }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '1rem' }}>
                                {subject.name}
                              </Typography>
                              <Typography sx={{ color: '#888', fontSize: '0.78rem' }}>
                                {chapters.length} টি অধ্যায়
                              </Typography>
                            </Box>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 3 }}>
                          {chapters.map((ch, ci) => (
                            <Stack key={ci} direction="row" spacing={2} alignItems="center"
                              sx={{
                                py: 1.5, px: 2, borderRadius: 2, mb: 0.5,
                                '&:hover': { background: `${color}06` },
                              }}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                background: `${color}10`, flexShrink: 0,
                              }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color }}>
                                  {ci + 1}
                                </Typography>
                              </Box>
                              <Typography sx={{ color: '#444', fontSize: '0.9rem' }}>{ch.chapterName}</Typography>
                            </Stack>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                      );
                    })}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box className="fade-up">
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1e1b4b', mb: 3 }}>
                      শিক্ষক মণ্ডলী
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2.5 }}>
                      {(teachers.length > 0 ? teachers : TEACHERS).map((t, i) => (
                        <Box key={i} sx={{
                          textAlign: 'center', p: 3, borderRadius: 3, background: '#fff',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                          transition: 'transform 0.3s',
                          '&:hover': { transform: 'translateY(-4px)' },
                        }}>
                          <Avatar src={config.BASE_API_URL + t.image}
                            sx={{
                              width: 90, height: 90, mx: 'auto', mb: 2,
                              border: '3px solid rgba(67,56,202,0.15)',
                            }} />
                          <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '0.95rem' }}>
                            {t.name}
                          </Typography>
                          <Typography sx={{ color: '#7c3aed', fontSize: '0.78rem', fontWeight: 600 }}>
                            {t.designation || t.subjects || t.role || ''}
                          </Typography>
                          {t.institution && (
                            <Typography sx={{ color: '#888', fontSize: '0.72rem', mt: 0.5 }}>
                              {t.institution}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* RIGHT - Pricing Sidebar */}
              <Box sx={{
                width: { xs: '100%', md: 340 }, flexShrink: 0,
                position: { md: 'sticky' }, top: { md: 90 },
              }}>
                <Card sx={{
                  borderRadius: 4, overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(67,56,202,0.12)',
                  border: '1px solid rgba(67,56,202,0.08)',
                }}>
                  {/* Price header */}
                  <Box sx={{ p: 3, background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)' }}>
                    <Typography sx={{ color: '#888', fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                      কোর্স ফি
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="baseline">
                      <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: '#4338ca' }}>
                        ৳{finalPrice}
                      </Typography>
                      {hasDiscount && (
                        <Typography sx={{
                          fontSize: '1rem', color: '#aaa', fontWeight: 600,
                          textDecoration: 'line-through',
                        }}>
                          ৳{originalPrice}
                        </Typography>
                      )}
                    </Stack>
                    {hasDiscount && (
                      <Chip size="small"
                        label={`${Math.round((1 - finalPrice / originalPrice) * 100)}% ছাড়`}
                        sx={{
                          mt: 1, background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                          color: '#fff', fontWeight: 700, fontSize: '0.75rem',
                        }} />
                    )}
                  </Box>

                  {/* CTA */}
                  <Box sx={{ px: 3, py: 2.5 }}>
                    <Button fullWidth variant="contained"
                      onClick={() => isInDashboard
                        ? navigate(getDashboardBase() + '/courseDetailsAndEnrollments/?courseId=' + courseId)
                        : navigate('/login')
                      }
                      sx={{
                        py: 1.8, borderRadius: 3, fontWeight: 800, fontSize: '1rem',
                        background: 'linear-gradient(135deg, #4338ca, #6366f1)',
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(67,56,202,0.35)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #3730a3, #4f46e5)',
                          boxShadow: '0 6px 24px rgba(67,56,202,0.45)',
                        },
                      }}>
                      📋 কোর্সে ভর্তি হও
                    </Button>
                  </Box>

                  <Divider />

                  {/* Course Info */}
                  <Box sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: '0.9rem', mb: 2 }}>
                      কোর্সের তথ্য
                    </Typography>
                    {[
                      { icon: 'mdi:clock-outline', label: 'সময়কাল', value: get(course, 'time', get(course, 'duration', '') + ' দিন') },
                      { icon: 'mdi:tag-outline', label: 'ক্যাটাগরি', value: get(course, 'type', 'Medical') },
                      { icon: 'mdi:credit-card-outline', label: 'পেমেন্ট', value: get(course, 'paymentType', 'Offline') },
                      { icon: 'mdi:earth', label: 'প্রকাশ', value: get(course, 'publishType', 'Internal') },
                    ].map((info, i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="center"
                        sx={{ py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                        <Icon icon={info.icon} style={{ fontSize: 20, color: '#6366f1' }} />
                        <Typography sx={{ color: '#888', fontSize: '0.82rem', flex: 1 }}>{info.label}</Typography>
                        <Typography sx={{ color: '#333', fontSize: '0.82rem', fontWeight: 600 }}>{info.value}</Typography>
                      </Stack>
                    ))}
                  </Box>

                  {/* Back button */}
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Button fullWidth variant="outlined"
                      onClick={() => isInDashboard
                        ? navigate(getDashboardBase() + '/courseList')
                        : navigate('/coolHomePage')
                      }
                      startIcon={<Icon icon="mdi:arrow-left" />}
                      sx={{
                        py: 1.2, borderRadius: 3, fontWeight: 600,
                        borderColor: 'rgba(67,56,202,0.2)', color: '#4338ca',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#4338ca', background: 'rgba(67,56,202,0.04)' },
                      }}>
                      সকল কোর্স দেখুন
                    </Button>
                  </Box>
                </Card>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* Editor Modals */}
      <FeaturesEditor open={featuresEditorOpen} onClose={() => setFeaturesEditorOpen(false)}
        features={get(course, 'features', [])} onSave={saveFeatures} />
      <SyllabusEditor open={syllabusEditorOpen} onClose={() => setSyllabusEditorOpen(false)}
        syllabusEntries={get(course, 'syllabusEntries', [])} onSave={saveSyllabus} />

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Page>
  );
}
